import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Combobox, ComboboxItem } from '@node-space/ui-components'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'

interface RewardsCurrencySelectProps {
  selectedCurrency?: string
  disabled?: boolean
  filterByBalance?: boolean
  setMaxAmount?: (value: string) => void
}

export const RewardsCurrencySelect = ({
  selectedCurrency,
  disabled = false,
  setMaxAmount,
  filterByBalance,
}: RewardsCurrencySelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const { userPortfolios, rewardsProducts } = useRewardsSheetContext()

  const currencyOptions = useMemo<ComboboxItem[]>(() => {
    if (!rewardsProducts?.length) return []

    let availableCurrencies = rewardsProducts.map(product => product.currency)

    // If filterByBalance is set, only show currencies with balance > 0
    if (filterByBalance && userPortfolios?.length) {
      availableCurrencies = availableCurrencies.filter(currency => {
        const portfolio = userPortfolios.find(p => p.currency === currency)
        const balance = Number(portfolio?.balance) || 0
        return balance > 0
      })
    }

    return availableCurrencies.map(currency => ({
      value: currency,
      leftText: currency,
      LeftElement: <Avatar name={currency} size="small" />,
    }))
  }, [rewardsProducts, userPortfolios, filterByBalance])

  const handleSetMaxAmount = (value: string) => {
    if (setMaxAmount && userPortfolios?.length) {
      const portfolio = userPortfolios.find(portfolio => portfolio.currency === value)
      const portfolioBalance = portfolio?.balance

      setMaxAmount(portfolioBalance || '0')
    }
  }

  // Set max amount in case where currency is selected
  useEffect(() => {
    if (disabled && selectedCurrency) {
      handleSetMaxAmount(selectedCurrency)
    }
  }, [currencyOptions])

  return (
    <Controller
      control={control}
      name="currency"
      render={({ field: { value, ...field }, fieldState: { error } }) => (
        <Combobox
          {...field}
          onChange={value => {
            handleSetMaxAmount(value)
            field.onChange(value)
          }}
          disabled={disabled}
          id="currency"
          label={t('currency')}
          selectPlaceholder={t('select')}
          items={currencyOptions}
          defaultValue={value}
          errorText={error?.message}
          testId="currency-combobox"
        />
      )}
    />
  )
}
